/**
 * Waiting-area queue TV board.
 * Completely independent of the counter customer display at /display/:storeId.
 * Never switches modes — renders only the walk-in queue.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchPublicQueueDisplay,
  type PublicQueueDisplay,
} from '@/lib/api/public-display.js';
import {
  createReconnectingWebSocket,
  type WsConnectionStatus,
} from '@/lib/ws-reconnect.js';

const STALE_AFTER_MS = 25_000;

export function QueueDisplayPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const [board, setBoard] = useState<PublicQueueDisplay | null>(null);
  const [wsStatus, setWsStatus] = useState<WsConnectionStatus>('connecting');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(0);
  const [now, setNow] = useState(() => Date.now());
  const servingKeyRef = useRef('');

  const applyBoard = useCallback((next: PublicQueueDisplay | null) => {
    if (!next || next.enabled === false) {
      setBoard(next ?? { enabled: false });
      setLastUpdatedAt(Date.now());
      return;
    }
    const servingKey = (next.serving ?? [])
      .map((s) => s.id)
      .sort()
      .join(',');
    const prev = servingKeyRef.current;
    if (prev && servingKey && prev !== servingKey && next.audioAlert) {
      try {
        const ctx = new AudioContext();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.frequency.value = 880;
        g.gain.value = 0.1;
        o.start();
        window.setTimeout(() => {
          o.frequency.value = 1175;
        }, 120);
        o.stop(ctx.currentTime + 0.28);
      } catch {
        /* autoplay blocked */
      }
    }
    servingKeyRef.current = servingKey;
    setBoard(next);
    setLastUpdatedAt(Date.now());
  }, []);

  // Immediate + 10s polling fallback (never rely on WS alone)
  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    const tick = () => {
      void fetchPublicQueueDisplay(storeId)
        .then((b) => {
          if (!cancelled) applyBoard(b);
        })
        .catch(() => {
          /* keep last good board */
        });
    };
    tick();
    const id = window.setInterval(tick, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [storeId, applyBoard]);

  // Dedicated queue-display WebSocket — never joins display:{storeId}
  useEffect(() => {
    if (!storeId) return;
    const socket = createReconnectingWebSocket({
      path: `/ws/queue-display/${storeId}`,
      maxFailures: 10_000,
      onStatus: setWsStatus,
      onMessage: (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type?: string;
            payload?: PublicQueueDisplay;
          };
          if (msg.type === 'ping') return;
          if (msg.type === 'queue_board.update' && msg.payload) {
            applyBoard(msg.payload);
          }
        } catch {
          /* ignore */
        }
      },
    });
    return () => socket.close();
  }, [storeId, applyBoard]);

  // Screen Wake Lock (prevent sleep on quiet shop floors)
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;

    async function requestLock() {
      try {
        if (!('wakeLock' in navigator)) return;
        lock = await navigator.wakeLock.request('screen');
      } catch {
        /* unsupported / denied */
      }
    }

    void requestLock();
    const onVis = () => {
      if (document.visibilityState === 'visible' && !cancelled) void requestLock();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      void lock?.release();
    };
  }, []);

  // Clock for stale indicator
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, []);

  // Hide system cursor on this full-screen TV page
  useEffect(() => {
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.cursor = prev;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  if (!storeId) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950 text-white">
        Invalid queue display URL
      </div>
    );
  }

  const isStale =
    lastUpdatedAt > 0 && now - lastUpdatedAt > STALE_AFTER_MS && wsStatus !== 'open';
  const hasPeople =
    Boolean(board?.enabled) &&
    ((board?.serving?.length ?? 0) > 0 || (board?.waiting?.length ?? 0) > 0);
  const storeName = board?.storeName?.trim() || 'Welcome';
  const emptyLine =
    board?.emptyHeadline?.trim() ||
    board?.idleMessage?.trim() ||
    "No wait — we'll be right with you";

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-slate-950 text-white"
      style={{ cursor: 'none' }}
    >
      {/* Subtle live / stale chip — unobtrusive corner */}
      <div className="absolute right-5 top-4 z-10 flex items-center gap-2 text-sm font-medium text-slate-400">
        <span
          className={[
            'inline-block h-2.5 w-2.5 rounded-full',
            isStale
              ? 'bg-amber-400'
              : wsStatus === 'open'
                ? 'bg-emerald-400'
                : 'animate-pulse bg-slate-500',
          ].join(' ')}
        />
        {isStale
          ? 'Updating…'
          : wsStatus === 'open'
            ? 'Live'
            : wsStatus === 'failed'
              ? 'Polling'
              : 'Connecting'}
      </div>

      <header className="flex shrink-0 items-center justify-center gap-6 px-10 pt-10 pb-4">
        {board?.logoUrl ? (
          <img
            src={board.logoUrl}
            alt=""
            className="h-16 w-auto max-w-[200px] object-contain opacity-90"
          />
        ) : null}
        <h1 className="text-center text-3xl font-bold tracking-tight text-slate-100 md:text-4xl">
          {storeName}
        </h1>
      </header>

      {!board ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-3xl text-slate-400">Loading queue…</p>
        </div>
      ) : board.enabled === false ? (
        <div className="flex flex-1 flex-col items-center justify-center px-12 text-center">
          <p className="text-5xl font-bold text-slate-200">Queue display</p>
          <p className="mt-6 max-w-2xl text-2xl text-slate-400">
            Walk-in queue is not enabled for this store. Turn it on in Settings → Customer Display.
          </p>
        </div>
      ) : !hasPeople ? (
        <div className="flex flex-1 flex-col items-center justify-center px-12 text-center">
          <p className="text-6xl font-bold tracking-tight text-white md:text-7xl">{emptyLine}</p>
          <p className="mt-8 text-3xl font-medium text-slate-400">
            No wait — we&apos;ll be right with you
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col px-12 pb-12 pt-4">
          <section className="shrink-0 border-b border-white/10 pb-10">
            <p className="text-center text-2xl font-bold uppercase tracking-[0.35em] text-emerald-400 md:text-3xl">
              Now serving
            </p>
            {(board.serving?.length ?? 0) === 0 ? (
              <p className="mt-8 text-center text-5xl font-semibold text-slate-500">—</p>
            ) : (
              <ul className="mt-8 space-y-4 text-center">
                {(board.serving ?? []).map((p) => (
                  <li key={p.id}>
                    <p className="text-7xl font-black leading-none tracking-tight text-white md:text-8xl lg:text-9xl">
                      {p.displayName}
                    </p>
                    {(board.showReason && p.reason) || (board.showDevice && p.deviceLabel) ? (
                      <p className="mt-3 text-3xl font-medium text-slate-300 md:text-4xl">
                        {[board.showReason ? p.reason : null, board.showDevice ? p.deviceLabel : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-10 min-h-0 flex-1">
            <p className="text-center text-2xl font-bold uppercase tracking-[0.35em] text-sky-400 md:text-3xl">
              Next up
            </p>
            {(board.waiting?.length ?? 0) === 0 ? (
              <p className="mt-10 text-center text-4xl font-semibold text-slate-300">
                You&apos;re next
              </p>
            ) : (
              <ul className="mx-auto mt-8 max-w-5xl space-y-5">
                {(board.waiting ?? []).map((p) => (
                  <li
                    key={p.id}
                    className="flex items-baseline justify-between gap-8 border-b border-white/5 pb-4"
                  >
                    <span className="min-w-0 text-5xl font-bold leading-tight text-white md:text-6xl">
                      {p.displayName}
                      {board.showReason && p.reason ? (
                        <span className="ml-3 text-2xl font-medium text-slate-400 md:text-3xl">
                          {p.reason}
                        </span>
                      ) : null}
                    </span>
                    {p.estimatedWaitMinutes != null ? (
                      <span className="shrink-0 text-4xl font-semibold text-slate-300 md:text-5xl">
                        ~{p.estimatedWaitMinutes} min
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {(board.waitingMore ?? 0) > 0 ? (
              <p className="mt-6 text-center text-3xl text-slate-400">
                +{board.waitingMore} more
              </p>
            ) : null}
          </section>

          {board.averageWaitMinutes != null ? (
            <p className="shrink-0 pt-6 text-center text-2xl text-slate-500">
              Average service today: {board.averageWaitMinutes} minutes
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
